import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { JSONSchema7Object } from 'json-schema';

import { ExpressionRule, LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { KeyValue, StringKeyValue, TemplateFilter } from 'dashboard/lib/workflows/objects';
import { ExpirationIntervals } from 'dashboard/models/inquiry-template-version';
import { Trigger, TriggerObjectType, TriggerRecurringAttributes } from 'dashboard/models/trigger';
import { Verification } from 'dashboard/models/verification';
import { WorkflowFieldObjectKey, WorkflowFieldVariableKey } from 'dashboard/models/workflow';
import { BlockInput } from 'dashboard/models/workflow-block';
import { GenericWorkflowAction } from 'dashboard/models/workflow-schema';
import { EventName } from 'lib/constants/events';
import { FieldSchemaJson } from 'lib/fields';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { JsonSchema, JsonSchemaArray, JsonSchemaTypes } from 'lib/json-schema';
import { generateWorkflowToken } from 'lib/workflows';

export enum WorkflowVersionStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived',
  Created = 'created',
  Pending = 'pending',
  Completed = 'completed',
}

export enum WorkflowVersionDeploymentStatus {
  Treatment = 'treatment',
  Control = 'control',
}

// TODO: Consolidate WorkflowEnvironment into a DashboardEnvironment type.
export enum WorkflowEnvironment {
  Production = 'production',
  Sandbox = 'sandbox',
}

export interface WorkflowVersionAttributes {
  'created-at': string;
  'updated-at': string;
  description?: string;
  status: WorkflowVersionStatus;
  'steps-config': WorkflowStep[];
  outputs: WorkflowVersionOutput[];
  enhanced: boolean;
  'workflow-name'?: string;
  'time-out-minutes'?: number;
  'last-updater'?: string;
  'edited-at'?: string;
  'published-at'?: string;
  'template-filters': TemplateFilter[];
}

export interface WorkflowVersionRelationships {
  workflow: ApiRelationship;
  trigger: ApiRelationship;
  'active-deployment'?: ApiRelationship;
}

export type WorkflowVersion = ApiResource<
  WorkflowVersionAttributes,
  WorkflowVersionRelationships,
  ApiModel.WorkflowVersion
>;

export interface WorkflowVersionOutput {
  token: string;
  name: string;
  'method-chain': string;
}

///////////////////
// Workflows 2.0 //
///////////////////

/*
 * These constants represent the names of workflow step actions and must be
 * in sync with the action names in the Backend.
 *
 * The first word of the key should _always_ be a verb that describes
 * the action
 *
 * The variable name should _always_ be the camel-cased version of the key.
 * Conversely, the key should _always_ be the snake-cased version of the variable
 * name
 *
 * (There are a few actions that do not follow these guidelines, but they should
 * not be copied)
 */
export enum WorkflowStepActionKey {
  AddCaseToCaseQueue = 'add_case_to_case_queue',
  AddCommentToCase = 'add_comment_to_case',
  AddItemToList = 'add_item_to_list',
  AddItemToListFace = 'add_item_to_list_face',
  ApproveInquiry = 'approve_inquiry',
  AssignCase = 'assign_case',
  AttachObjects = 'attach_objects',
  AttachObjectsToCase = 'attach_objects_to_case',
  AttachObjectsToSAR = 'attach_objects_to_sar',
  AttemptConfirmationCode = 'attempt_confirmation_code',
  AutofillAttachedObjectsToSAR = 'autofill_attached_objects_to_sar',
  Calculate = 'calculate',
  ChainalysisAddressScreening = 'run_chainalysis_address_screening',
  ClearbitBusinessLookup = 'run_clearbit_business_lookup',
  ClearCaseSla = 'clear_case_sla',
  CoinbaseCheckCryptoRisk = 'run_coinbase_check_crypto_risk',
  ConfirmLatestVerificationEmailAddress = 'confirm_latest_verification_email_address',
  ConfirmLatestVerificationPhoneNumber = 'confirm_latest_verification_phone_number',
  CopyTags = 'copy_tags',
  CreateCase = 'create_case',
  CreateInquiry = 'create_inquiry',
  CreateSAR = 'create_sar',
  CreateTransaction = 'create_transaction',
  CreateDocumentGovernmentId = 'create_document_government_id',
  CreateVerificationAamvaFromInquiry = 'create_verification_aamva_from_inquiry',
  CreateVerificationCertificateKoreaFromInquiry = 'create_verification_certificate_korea_from_inquiry',
  CreateVerificationDatabaseEcbsvFromInquiry = 'create_verification_database_ecbsv_from_inquiry',
  CreateVerificationDatabaseFromInquiry = 'create_verification_database_from_inquiry',
  CreateVerificationDatabaseAadhaarFromInquiry = 'create_verification_database_aadhaar_from_inquiry',
  CreateVerificationDatabaseStandardFromInquiry = 'create_verification_database_standard_from_inquiry',
  CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry = 'create_verification_digital_id_finnish_trust_network_from_inquiry',
  CreateVerificationDigitalIdOneIdFromInquiry = 'create_verification_digital_id_one_id_from_inquiry',
  CreateVerificationDigitalIdSingpassFromInquiry = 'create_verification_digital_id_singpass_from_inquiry',
  CreateVerificationDigitalIdUkSharecodeFromInquiry = 'create_verification_digital_id_uk_sharecode_from_inquiry',
  CreateVerificationDocumentFromInquiry = 'create_verification_document_from_inquiry',
  CreateVerificationEmailAddressFromInquiry = 'create_verification_email_address_from_inquiry',
  CreateVerificationGovernmentIdFromInquiry = 'create_verification_government_id_from_inquiry',
  CreateVerificationGovernmentIdNfcFromInquiry = 'create_verification_government_id_nfc_from_inquiry',
  CreateVerificationPhoneNumberFromInquiry = 'create_verification_phone_number_from_inquiry',
  CreateVerificationSelfieFromInquiry = 'create_verification_selfie_from_inquiry',
  DeclineInquiry = 'decline_inquiry',
  EmailInquiryLink = 'email_inquiry_link',
  EquifaxOneview = 'run_equifax_oneview',
  EvaluateCode = 'evaluate_code',
  FetchPersonaObject = 'fetch_persona_object',
  FindOrCreateAccount = 'find_or_create_account',
  FindOrCreateTransaction = 'find_or_create_transaction',
  GPTGenerateMessage = 'gpt_generate_message',
  HttpRequest = 'http_request',
  HubspotOAuthCreateObject = 'hubspot_oauth_create_object',
  HubspotOAuthSearchObject = 'hubspot_oauth_search_object',
  HubspotOAuthUpdateObject = 'hubspot_oauth_update_object',
  HubspotUpdateObject = 'hubspot_update_object',
  LabelObject = 'label_object',
  ListMatch = 'test_list_match',
  MarkInquiryForReview = 'mark_inquiry_for_review',
  MxAccount = 'run_mx_account',
  MxCreateUser = 'mx_create_user',
  MxGetMicrodepositStatus = 'mx_get_microdeposit_status',
  MxRequestConnectWidget = 'mx_request_connect_widget',
  NovaCreditCashAtlas = 'run_nova_credit_cash_atlas',
  NovaCreditCreditPassport = 'run_nova_credit_credit_passport',
  PauseReportsContinuousMonitoring = 'pause_reports_continuous_monitoring',
  ProcessLatestVerificationCertificateKorea = 'process_latest_verification_certificate_korea',
  ProcessLatestVerificationDatabaseAadhaar = 'process_latest_verification_database_aadhaar',
  ProcessLatestVerificationDigitalIdFinnishTrustNetwork = 'process_latest_verification_digital_id_finnish_trust_network',
  ProcessLatestVerificationDigitalIdOneId = 'process_latest_verification_digital_id_one_id',
  ProcessLatestVerificationDigitalIdSingpass = 'process_latest_verification_digital_id_singpass',
  Query = 'query',
  RedactObject = 'redact_object',
  RemoveItemFromList = 'remove_item_from_list',
  ResendConfirmationCodeLatestVerificationEmailAddress = 'resend_confirmation_code_latest_verification_email_address',
  RemoveTag = 'remove_tag',
  ResendConfirmationCodeLatestVerificationPhoneNumber = 'resend_confirmation_code_latest_verification_phone_number',
  RunGraphQuery = 'run_graph_query',
  RunKyckrBusinessLookup = 'run_kyckr_business_lookup',
  RunHttpPlatformIntegration = 'run_http_platform_integration',
  RunReportAddressLookup = 'run_report_address_lookup',
  RunReportAdverseMedia = 'run_report_adverse_media',
  RunReportBusinessAdverseMedia = 'run_report_business_adverse_media',
  RunReportBusinessAssociatedPersons = 'run_report_business_associated_persons',
  RunReportBusinessClassification = 'run_report_business_classification',
  RunReportBusinessIndustryClassification = 'run_report_business_industry_classification',
  RunReportBusinessLiensLookup = 'run_report_business_liens_lookup',
  RunReportBusinessLookup = 'run_report_business_lookup',
  RunReportBusinessNonprofit = 'run_report_business_nonprofit',
  RunReportBusinessOnlinePresence = 'run_report_business_online_presence',
  RunReportBusinessRegistrationsLookup = 'run_report_business_registrations_lookup',
  RunReportBusinessSocialMedia = 'run_report_business_social_media',
  RunReportBusinessWatchlist = 'run_report_business_watchlist',
  RunReportBusinessWebsite = 'run_report_business_website',
  RunReportCryptoAddressWatchlist = 'run_report_crypto_address_watchlist',
  RunReportCustomList = 'run_report_custom_list',
  RunReportEmailAddress = 'run_report_email_address',
  RunReportGeneric = 'run_report',
  RunReportPhoneNumber = 'run_report_phone_number',
  RunReportPoliticallyExposedPerson = 'run_report_politically_exposed_person',
  RunReportProfile = 'run_report_profile',
  RunReportProfileNonAuthoritative = 'run_report_profile_non_authoritative',
  RunReportScreening = 'run_report_screening',
  RunReportSentilinkApplicationRisk = 'run_sentilink_application_risk',
  RunReportSentilinkScores = 'run_sentilink_scores',
  RunReportSocialMedia = 'run_report_social_media',
  RunReportSynthetic = 'run_report_synthetic',
  RunReportWatchlist = 'run_report_watchlist',
  RunReportWithParams = 'run_report_with_params',
  RunVerificationDatabaseBusiness = 'run_verification_database_business',
  RunVerificationDatabaseBusinessFootprintFromInquiry = 'run_verification_database_business_footprint_from_inquiry',
  RunVerificationBusinessWebsiteFromInquiry = 'run_verification_business_website_from_inquiry',
  RunVerificationDatabasePhoneCarrierFromInquiry = 'run_verification_database_phone_carrier_from_inquiry',
  RunVerificationDatabaseSerproFromInquiry = 'run_verification_database_serpro_from_inquiry',
  RunVerificationDatabaseTinFromInquiry = 'run_verification_database_tin_from_inquiry',
  RunVerificationDatabaseVatNumberFromInquiry = 'run_verification_database_vat_number_from_inquiry',
  RunVerificationGeneric = 'run_verification',
  SendCaseMessage = 'send_case_message',
  ScheduleWorkflowRun = 'schedule_workflow_run',
  SalesforceCreateObject = 'salesforce_create_object',
  SalesforceOAuthCreateRecord = 'salesforce_oauth_create_record',
  SalesforceOAuthSearchRecord = 'salesforce_oauth_search_record',
  SalesforceOAuthUpdateRecord = 'salesforce_oauth_update_record',
  SalesforceUpdateObject = 'salesforce_update_object',
  SendConfirmationCode = 'send_confirmation_code',
  SendEmail = 'send_email',
  SendSlackMessage = 'send_slack_message',
  SendSms = 'send_sms',
  SetCaseSla = 'set_case_sla',
  SetRelation = 'set_relation',
  SlackOAuthSendMessage = 'slack_oauth_send_message',
  SmsInquiryLink = 'sms_inquiry_link',
  SubmitMiddeskBusinessLookup = 'run_middesk_business_lookup',
  SyncGraph = 'sync_graph',
  TagObject = 'tag_object',
  TrmWalletScreening = 'run_trm_wallet_screening',
  TrustDeviceFromInquiry = 'trust_device_from_inquiry',
  UnsetRelation = 'unset_relation',
  UpdateAccount = 'update_account',
  UpdateAccountFieldsFromInquiry = 'update_account_fields_from_inquiry',
  UpdateAccountFromUserInput = 'update_account_from_user_input',
  UpdateAccountStatus = 'update_account_status',
  UpdateCaseStatus = 'update_case_status',
  UpdateInquiryFieldsFromVerificationDatabase = 'update_inquiry_fields_from_verification_database',
  UpdateInquiryFieldsFromVerificationDatabaseStandard = 'update_inquiry_fields_from_verification_database_standard',
  UpdateInquiryFieldsFromVerificationDocument = 'update_inquiry_fields_from_verification_document',
  UpdateInquiryFieldsFromVerificationGovernmentId = 'update_inquiry_fields_from_verification_government_id',
  StandardizeIdDetails = 'standardize_id_details',
  UpdateInquiryFromPersona = 'update_inquiry_from_persona',
  UpdateInquiryNextStepName = 'update_inquiry_next_step_name',
  UpdateInquiryStateDatabaseError = 'update_inquiry_state_database_error',
  UpdateInquiryStateDocumentError = 'update_inquiry_state_document_error',
  UpdateInquiryStateGovernmentIdError = 'update_inquiry_state_government_id_error',
  UpdateInquiryStateSelfieError = 'update_inquiry_state_selfie_error',
  UpdateObject = 'update_object',
  UpdateTransactionStatus = 'update_transaction_status',
  ZendeskCreateTicket = 'zendesk_create_ticket',
  ZendeskOAuthCreateTicket = 'zendesk_oauth_create_ticket',
  ZendeskOAuthUpdateTicket = 'zendesk_oauth_update_ticket',
  ZendeskUpdateTicket = 'zendesk_update_ticket',
  ZendeskAddTagsToTicket = 'zendesk_add_tags_to_ticket',
  ZendeskRemoveTagsFromTicket = 'zendesk_remove_tags_from_ticket',
}

type ResolvedInputs = Record<string, string>;

/*
 * These actions are readonly and cannot be configured. We keep them here so
 * they show up on the workflow runs page.
 */
export enum DeprecatedWorkflowStepActionKey {
  CreateReportAdverseMedia = 'create_report_adverse_media',
  CreateReportEmailAddress = 'create_report_email_address',
  CreateReportPhoneNumber = 'create_report_phone_number',
  CreateReportProfile = 'create_report_profile',
  CreateReportSocialMedia = 'create_report_social_media',
  CreateReportSynthetic = 'create_report_synthetic',
  CreateReportWatchlist = 'create_report_watchlist',
  ResolveCase = 'resolve_case',
  UpdateInquiryFieldsFromVerification = 'update_inquiry_fields_from_verification',
  GetAnalyticsMetric = 'get_analytics_metric',
}

export enum NullWorkflowStepActionKey {
  NullKey = 'null_workflow_action',
}

export type StaticWorkflowStepActionKey =
  | WorkflowStepActionKey
  | DeprecatedWorkflowStepActionKey
  | NullWorkflowStepActionKey;

export const ENHANCED_ACTIONS: StaticWorkflowStepActionKey[] = [
  WorkflowStepActionKey.ChainalysisAddressScreening,
  WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
  WorkflowStepActionKey.EquifaxOneview,
  WorkflowStepActionKey.EvaluateCode,
  WorkflowStepActionKey.HttpRequest,
  WorkflowStepActionKey.HubspotOAuthCreateObject,
  WorkflowStepActionKey.HubspotOAuthSearchObject,
  WorkflowStepActionKey.HubspotOAuthUpdateObject,
  WorkflowStepActionKey.HubspotUpdateObject,
  WorkflowStepActionKey.GPTGenerateMessage,
  WorkflowStepActionKey.LabelObject,
  WorkflowStepActionKey.MxAccount,
  WorkflowStepActionKey.MxGetMicrodepositStatus,
  WorkflowStepActionKey.MxCreateUser,
  WorkflowStepActionKey.MxRequestConnectWidget,
  WorkflowStepActionKey.NovaCreditCashAtlas,
  WorkflowStepActionKey.RunKyckrBusinessLookup,
  WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
  WorkflowStepActionKey.RunReportSentilinkScores,
  WorkflowStepActionKey.SalesforceCreateObject,
  WorkflowStepActionKey.SalesforceOAuthCreateRecord,
  WorkflowStepActionKey.SalesforceOAuthSearchRecord,
  WorkflowStepActionKey.SalesforceOAuthUpdateRecord,
  WorkflowStepActionKey.SalesforceUpdateObject,
  WorkflowStepActionKey.ScheduleWorkflowRun,
  WorkflowStepActionKey.SendEmail,
  WorkflowStepActionKey.SendSlackMessage,
  WorkflowStepActionKey.SlackOAuthSendMessage,
  WorkflowStepActionKey.SendSms,
  WorkflowStepActionKey.SubmitMiddeskBusinessLookup,
  WorkflowStepActionKey.TrmWalletScreening,
  WorkflowStepActionKey.ZendeskCreateTicket,
  WorkflowStepActionKey.ZendeskOAuthCreateTicket,
  WorkflowStepActionKey.ZendeskOAuthUpdateTicket,
  WorkflowStepActionKey.ZendeskUpdateTicket,
  WorkflowStepActionKey.ZendeskAddTagsToTicket,
  WorkflowStepActionKey.ZendeskRemoveTagsFromTicket,
];

// More detail see doc: https://www.notion.so/withpersona/FLE-Workflow-actions-gating-dock-472807e1d14c4a5988480e17426a1b5b
export const INQUIRY_ENHANCED_ACTIONS: WorkflowStepActionKey[] = [
  WorkflowStepActionKey.Calculate,
  WorkflowStepActionKey.EvaluateCode,
  WorkflowStepActionKey.HttpRequest,
  WorkflowStepActionKey.GPTGenerateMessage,
  WorkflowStepActionKey.MxAccount,
  WorkflowStepActionKey.MxGetMicrodepositStatus,
  WorkflowStepActionKey.MxCreateUser,
  WorkflowStepActionKey.MxRequestConnectWidget,
  WorkflowStepActionKey.SendEmail,
  WorkflowStepActionKey.SendSlackMessage,
  WorkflowStepActionKey.SlackOAuthSendMessage,
  WorkflowStepActionKey.SendSms,
];

export const INQUIRY_BASIC_ACTIONS: WorkflowStepActionKey[] = [
  WorkflowStepActionKey.AttachObjectsToCase,
  WorkflowStepActionKey.CopyTags,
  WorkflowStepActionKey.FetchPersonaObject,
  WorkflowStepActionKey.RemoveTag,
  WorkflowStepActionKey.TagObject,
  WorkflowStepActionKey.UpdateObject,
];

export const ACTIONS_THAT_CREATE_REPORTS: Array<StaticWorkflowStepActionKey> = [
  WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
  WorkflowStepActionKey.EquifaxOneview,
  WorkflowStepActionKey.MxAccount,
  WorkflowStepActionKey.RunKyckrBusinessLookup,
  WorkflowStepActionKey.RunReportAddressLookup,
  WorkflowStepActionKey.RunReportAdverseMedia,
  WorkflowStepActionKey.RunReportBusinessAdverseMedia,
  WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
  WorkflowStepActionKey.RunReportBusinessClassification,
  WorkflowStepActionKey.RunReportBusinessIndustryClassification,
  WorkflowStepActionKey.RunReportBusinessWatchlist,
  WorkflowStepActionKey.RunReportBusinessLookup,
  WorkflowStepActionKey.RunReportBusinessSocialMedia,
  WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
  WorkflowStepActionKey.RunReportCustomList,
  WorkflowStepActionKey.RunReportEmailAddress,
  WorkflowStepActionKey.RunReportPhoneNumber,
  WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
  WorkflowStepActionKey.RunReportProfile,
  WorkflowStepActionKey.RunReportProfileNonAuthoritative,
  WorkflowStepActionKey.RunReportScreening,
  WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
  WorkflowStepActionKey.RunReportSentilinkScores,
  WorkflowStepActionKey.RunReportSocialMedia,
  WorkflowStepActionKey.RunReportSynthetic,
  WorkflowStepActionKey.RunReportWatchlist,
  WorkflowStepActionKey.RunReportGeneric,
  WorkflowStepActionKey.TrmWalletScreening,
];

export const ACTIONS_THAT_CREATE_EXTERNAL_INTEGRATION_REPORTS: Array<AllWorkflowAction['key']> = [
  WorkflowStepActionKey.ChainalysisAddressScreening,
  WorkflowStepActionKey.EquifaxOneview,
  WorkflowStepActionKey.MxAccount,
  WorkflowStepActionKey.RunKyckrBusinessLookup,
  WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
  WorkflowStepActionKey.RunReportSentilinkScores,
  WorkflowStepActionKey.TrmWalletScreening,
];

export function isCreateVerificationAction(
  workflowAction: AllWorkflowAction,
): workflowAction is WorkflowRunVerificationAction {
  switch (workflowAction.key) {
    case WorkflowStepActionKey.CreateVerificationAamvaFromInquiry:
    case WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry:
    case WorkflowStepActionKey.CreateVerificationDocumentFromInquiry:
    case WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry:
    case WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry:
    case WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry:
    case WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry:
    case WorkflowStepActionKey.CreateVerificationSelfieFromInquiry:
    case WorkflowStepActionKey.RunVerificationDatabaseBusiness:
    case WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry:
    case WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry:
    case WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry:
    case WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry:
    case WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry:
    case WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry:
    case WorkflowStepActionKey.RunVerificationGeneric:
      return true;
    default:
      return false;
  }
}

export function isCreateVerificationActionWithFieldKey(
  workflowAction: AllWorkflowAction | undefined,
): workflowAction is WorkflowRunVerificationActionWithFieldKey {
  return (
    workflowAction?.params != null &&
    Object.keys(workflowAction.params).some((key) => key.startsWith('field-key'))
  );
}

export function isCreateVerificationActionWithFieldMapping(
  workflowAction: AllWorkflowAction | undefined,
): workflowAction is WorkflowRunVerificationActionWithFieldMapping {
  return (
    workflowAction?.params != null &&
    'verification-to-inquiry-field-mapping' in workflowAction.params
  );
}

export function isDynamicFlowFieldsReferenceAction(
  workflowAction: AllWorkflowAction | undefined,
): workflowAction is WorkflowDynamicFlowFieldsReferenceAction {
  switch (workflowAction?.key) {
    case WorkflowStepActionKey.StandardizeIdDetails:
    case WorkflowStepActionKey.UpdateAccountFieldsFromInquiry:
    case WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase:
    case WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabaseStandard:
    case WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId:
    case WorkflowStepActionKey.UpdateObject:
      return true;
    default:
      return (
        isCreateVerificationActionWithFieldMapping(workflowAction) ||
        isCreateVerificationActionWithFieldKey(workflowAction)
      );
  }
}

export type WorkflowRunVerificationAction =
  | WorkflowActionCreateVerificationAamvaFromInquiryV1
  | WorkflowActionCreateVerificationCertificateKoreaFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseEcbsvFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseAadhaarFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseStandardFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdFinnishTrustNetworkFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdOneIdFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdSingpassFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdUkSharecodeFromInquiryV1
  | WorkflowActionCreateVerificationDocumentFromInquiryV1
  | WorkflowActionCreateVerificationEmailAddressFromInquiryV1
  | WorkflowActionCreateVerificationGovernmentIdFromInquiryV1
  | WorkflowActionCreateVerificationGovernmentIdNfcFromInquiryV1
  | WorkflowActionCreateVerificationPhoneNumberFromInquiryV1
  | WorkflowActionCreateVerificationSelfieFromInquiryV1
  | WorkflowActionRunVerificationDatabaseBusinessV1
  | WorkflowActionRunVerificationDatabaseBusinessFootprintV1
  | WorkflowActionRunVerificationBusinessWebsiteFromInquiryV1
  | WorkflowActionRunVerificationDatabasePhoneCarrierFromInquiryV1
  | WorkflowActionRunVerificationDatabaseSerproFromInquiryV1
  | WorkflowActionRunVerificationDatabaseTinFromInquiryV1
  | WorkflowActionRunVerificationDatabaseVatNumberFromInquiryV1
  | WorkflowActionRunVerificationGenericV1;

// Workflow actions which run a verification and reference a single field from params 'field-key-[name]'
// 2 have both field key and mapping references - certificate korea and db aadhaar
type WorkflowRunVerificationActionWithFieldKey =
  | WorkflowActionCreateVerificationCertificateKoreaFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseAadhaarFromInquiryV1
  | WorkflowActionCreateVerificationDocumentFromInquiryV1
  | WorkflowActionCreateVerificationGovernmentIdFromInquiryV1
  | WorkflowActionCreateVerificationGovernmentIdNfcFromInquiryV1
  | WorkflowActionCreateVerificationSelfieFromInquiryV1;

// Workflow actions which run a verification and contain 'verification-to-inquiry-field-mapping' within params
type WorkflowRunVerificationActionWithFieldMapping =
  | WorkflowActionCreateVerificationAamvaFromInquiryV1
  | WorkflowActionCreateVerificationCertificateKoreaFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseAadhaarFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseEcbsvFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseStandardFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdFinnishTrustNetworkFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdOneIdFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdSingpassFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdUkSharecodeFromInquiryV1
  | WorkflowActionCreateVerificationEmailAddressFromInquiryV1
  | WorkflowActionCreateVerificationPhoneNumberFromInquiryV1
  | WorkflowActionRunVerificationDatabaseBusinessV1
  | WorkflowActionRunVerificationDatabaseBusinessFootprintV1
  | WorkflowActionRunVerificationBusinessWebsiteFromInquiryV1
  | WorkflowActionRunVerificationDatabasePhoneCarrierFromInquiryV1
  | WorkflowActionRunVerificationDatabaseSerproFromInquiryV1
  | WorkflowActionRunVerificationDatabaseTinFromInquiryV1
  | WorkflowActionRunVerificationDatabaseVatNumberFromInquiryV1;

export type WorkflowUpdateInquiryFromVerificationAction =
  | WorkflowActionUpdateInquiryFieldsFromVerificationDatabase
  | WorkflowActionUpdateInquiryFieldsFromVerificationDatabaseStandard
  | WorkflowActionUpdateInquiryFieldsFromVerificationGovernmentId;

export type WorkflowDynamicFlowFieldsReferenceAction =
  | WorkflowActionUpdateAccountFieldsFromInquiryV1
  | WorkflowRunVerificationActionWithFieldMapping
  | WorkflowRunVerificationActionWithFieldKey
  | WorkflowActionStandardizeIdDetailsV1
  | WorkflowUpdateInquiryFromVerificationAction
  | WorkflowActionUpdateObjectV1;

export interface WorkflowCreateVerificationParams {
  inquiry: string;
  'verification-template-version-id': string;
}

interface WorkflowCreateVerificationWithFieldsParams extends WorkflowCreateVerificationParams {
  'verification-to-inquiry-field-mapping': Record<string, string>;
}

interface WorkflowActionBase {
  key: StaticWorkflowStepActionKey;
  version: number;
  params: unknown;
}

export interface WorkflowActionListMatchV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ListMatch;
  version: 1;
  params: {
    'list-id': string;
    'list-item-fields': ResolvedInputs;
    'list-matchable'?: string;
  };
}

export interface WorkflowActionAddCommentToCaseV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AddCommentToCase;
  version: 1;
  params: {
    case: string;
    comment: string;
  };
}

export interface WorkflowActionAddItemToListV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AddItemToList;
  version: 1;
  params: {
    'list-id': string;
    'list-item-fields': ResolvedInputs;
  };
}

export interface WorkflowActionRemoveItemFromListV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RemoveItemFromList;
  version: 1;
  params: {
    'list-id': string;
    'list-item-fields': ResolvedInputs;
  };
}

export interface WorkflowActionAddItemToListFaceV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AddItemToListFace;
  version: 1;
  params: {
    'list-id': string;
    object: string;
  };
}

export interface WorkflowActionApproveInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ApproveInquiry;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionAddCaseToCaseQueueV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AddCaseToCaseQueue;
  version: 1;
  params: {
    case: string;
    'case-queue-token': string;
  };
}

export interface NullWorkflowAction extends WorkflowActionBase {
  key: NullWorkflowStepActionKey.NullKey;
  version: 1;
  params: Record<string, never>;
}

export interface WorkflowActionAssignCaseV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AssignCase;
  version: 1;
  params: {
    case: string;
    'assignee-type': 'individual' | 'assignment_policy';
    'assignee-user-token': string;
    'assignment-policy-token': string;
  };
}

export type VariableType =
  | JsonSchemaTypes.Number
  | JsonSchemaTypes.String
  | JsonSchemaTypes.Boolean;

export type VariableConfig = {
  id: string;
  name: string;
  type: VariableType;
  value: ExpressionRule;
};

export interface WorkflowActionAttachObjectsV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AttachObjects;
  version: 1;
  params: {
    target: string;
    objects: string[];
  };
}

export interface WorkflowActionCalculateV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.Calculate;
  version: 1;
  params: {
    'return-variables': string[];
    variables: VariableConfig[];
  };
}

export interface WorkflowActionConfirmLatestVerificationEmailAddress extends WorkflowActionBase {
  key: WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress;
  version: 1;
  params: {
    inquiry: string;
    'max-attempts': string;
  };
}

export interface WorkflowActionConfirmLatestVerificationPhoneNumber extends WorkflowActionBase {
  key: WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber;
  version: 1;
  params: {
    inquiry: string;
    'max-attempts': string;
  };
}

export interface WorkflowActionFindOrCreateAccountV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.FindOrCreateAccount;
  version: 1;
  params: {
    'account-type-token': string;
    'identifier-key': string;
    'reference-id': string;
  };
}

export interface WorkflowActionFindOrCreateTransactionV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.FindOrCreateTransaction;
  version: 1;
  params: {
    'transaction-type-token': string;
    'identifier-key': string;
    'identifier-value': string;
  };
}

export interface WorkflowActionCreateCaseV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateCase;
  version: 1;
  params: {
    'case-template-token': string;
    'case-objects': string[];
    'case-fields': ResolvedInputs;
  };
}

export interface WorkflowActionCreateSARV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateSAR;
  version: 1;
  params: {
    'sar-objects': string[];
  };
}

export interface WorkflowActionCreateTransactionV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateTransaction;
  version: 1;
  params: {
    'reference-id': string;
    'transaction-type-token': string;
    'transaction-fields': ResolvedInputs;
  };
}

export interface WorkflowActionCreateDocumentGovernmentIdV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateDocumentGovernmentId;
  version: 1;
  params: {
    objects?: string[];
    'verification-template-id': string;
    'verification-fields': Record<string, string>;
  };
}

export interface WorkflowActionChainalysisAddressScreeningV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ChainalysisAddressScreening;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateChainalysisAddressScreening; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionClearbitBusinessLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ClearbitBusinessLookup;
  version: 1;
  params: {
    'input-fields': {
      object: string;
      'email-address': string;
    };
  };
}

export interface WorkflowActionCoinbaseCheckCryptoRiskV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CoinbaseCheckCryptoRisk;
  version: 1;
  params: {
    'input-fields': {
      object: string;
      asset: string;
      address: string;
      'user-id': string;
      'partner-id': string;
    };
  };
}

export interface WorkflowActionMxAccountV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.MxAccount;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'input-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateMxAccount; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionRunReportSentilinkApplicationRiskV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportSentilinkApplicationRisk;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateSentilinkApplicationRisk; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionRunReportSentilinkScoresV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportSentilinkScores;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateSentilinkScores; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionNovaCreditCreditPassportV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.NovaCreditCreditPassport;
  version: 1;
  params: {
    'input-fields': {
      object: string;
      'product-id': string;
      'public-token': string;
    };
  };
}

export interface WorkflowActionEquifaxOneviewV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.EquifaxOneview;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateEquifaxOneview; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionRunKyckrBusinessLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunKyckrBusinessLookup;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateKyckrBusinessLookup; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionCreateReportAdverseMediaV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportAdverseMedia;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateReportEmailAddressV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportEmailAddress;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateReportPhoneNumberV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportPhoneNumber;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateReportProfileV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportProfile;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateReportSocialMediaV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportSocialMedia;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateReportSyntheticV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportSynthetic;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateReportWatchlistV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.CreateReportWatchlist;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionCreateVerificationAamvaFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationAamvaFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationCertificateKoreaFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'field-key-kica-flow-url': string;
  };
}

export interface WorkflowActionStandardizeIdDetailsV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.StandardizeIdDetails;
  version: 1;
  params: {
    verification: string;
  };
}

export interface WorkflowActionCreateVerificationDatabaseFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationDatabaseAadhaarFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'field-key-aadhaar-flow-url': string;
  };
}

export interface WorkflowActionCreateVerificationDatabaseStandardFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationDatabaseEcbsvFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationDigitalIdFinnishTrustNetworkFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'field-key-finnish-trust-network-flow-url': string;
  };
}

export interface WorkflowActionCreateVerificationDigitalIdOneIdFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'field-key-one-id-flow-url': string;
  };
}

export interface WorkflowActionCreateVerificationDigitalIdSingpassFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationDigitalIdUkSharecodeFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationDocumentFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationDocumentFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationParams & {
    'field-key-document': string;
    'complete-on-tentative': boolean;
  };
}

export interface WorkflowActionCreateVerificationEmailAddressFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'from-step': string;
    'from-component': string;
  };
}

export interface WorkflowActionCreateVerificationGovernmentIdFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationParams & {
    'field-key-document': string;
    'comparison-attribute-to-inquiry-field-map'?: Record<string, string>;
  };
}

export interface WorkflowActionCreateVerificationGovernmentIdNfcFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationParams & {
    'field-key-document': string;
  };
}

export interface WorkflowActionCreateVerificationPhoneNumberFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionCreateVerificationSelfieFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateVerificationSelfieFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationParams & {
    'field-key-selfie': string;
    'comparison-attribute-to-inquiry-field-map'?: Record<string, string>;
  };
}

export type WorkflowActionEvaluateCodeReturnType =
  | JsonSchemaTypes.Boolean
  | JsonSchemaTypes.Number
  | JsonSchemaTypes.String
  | JsonSchemaTypes.Object
  | 'void';

export interface WorkflowActionEvaluateCodeV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.EvaluateCode;
  version: 1;
  params: {
    trigger: string;
    'serialized-code': string;
    'return-type': Exclude<WorkflowActionEvaluateCodeReturnType, JsonSchemaTypes.Object>;
    'trigger-params'?: string;
    'retry-intervals'?: number[]; // TODO: (cody) promote to global config among action steps
  };
}

export interface WorkflowActionEvaluateCodeV2 extends WorkflowActionBase {
  key: WorkflowStepActionKey.EvaluateCode;
  version: 2;
  params: {
    'input-args': Record<string, string>;
    'return-schema': FieldSchemaJson;
    'serialized-code': string;
    timeout: number | null;
    nitro?: boolean;
    'retry-intervals'?: number[]; // TODO: (cody) promote to global config among action steps
  };
}

export interface WorkflowActionEmailInquiryLinkV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.EmailInquiryLink;
  version: 1;
  params: {
    inquiry: string;
    recipient?: string;
    subject: string;
    greeting: string;
    body: string;
    cta: string;
    'email-address': string;
  };
}

export interface WorkflowActionGPTGenerateMessageV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.GPTGenerateMessage;
  version: 1;
  params: {
    'external-integration-id': string;
    'gpt-model': string;
    prompt: string;
    temperature?: number;
    'top-p'?: number;
    'frequency-penalty'?: number;
    'presence-penalty'?: number;
    'max-tokens'?: number;
  };
}

export interface WorkflowActionSyncGraphV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SyncGraph;
  version: 1;
  params: {
    account: string;
    'use-selective-sync': boolean;
    'selective-sync-graph-query-template-ids': string[];
  };
}

export interface WorkflowActionQueryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.Query;
  version: 1;
  params: {
    'object-type': TriggerObjectType;
    filters: TriggerRecurringAttributes['filter'];
  };
}

export interface WorkflowActionMxCreateUserV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.MxCreateUser;
  version: 1;
  params: {
    'external-integration-id': string;
    'user-id': string;
    metadata: string;
    email: string;
  };
}

export interface WorkflowActionMxGetMicrodepositStatusV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.MxGetMicrodepositStatus;
  version: 1;
  params: {
    'external-integration-id': string;
    'user-guid': string;
    'microdeposit-guid': string;
  };
}

export interface WorkflowActionMxRequestConnectWidgetV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.MxRequestConnectWidget;
  version: 1;
  params: {
    'external-integration-id': string;
    'user-guid': string;
    'microdeposit-guid'?: string;
  };
}

export interface WorkflowActionNovaCreditCashAtlasV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.NovaCreditCashAtlas;
  version: 1;
  params: {
    'input-fields': {
      object: string;
      'product-id': string;
      'public-token': string;
    };
  };
}

export interface WorkflowActionPauseReportsContinuousMonitoringV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.PauseReportsContinuousMonitoring;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionProcessLatestVerificationCertificateKoreaV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionProcessLatestVerificationDatabaseAadhaarV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionProcessLatestVerificationDigitalIdFinnishTrustNetworkV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionProcessLatestVerificationDigitalIdOneIdV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionProcessLatestVerificationDigitalIdSingpassV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionRedactObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RedactObject;
  version: 1;
  params: {
    object: string;
    fields: {
      'template-id': string;
      path: string;
    }[];
    'redact-all': boolean;
    'retention-period-days': number;
  };
}

export interface WorkflowActionResendConfirmationCodeLatestVerificationEmailAddressV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationEmailAddress;
  version: 1;
  params: {
    inquiry: string;
    'from-step': string;
    'from-component': string;
  };
}

export interface WorkflowActionResendConfirmationCodeLatestVerificationPhoneNumberV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationPhoneNumber;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionResolveCaseV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.ResolveCase;
  version: 1;
  params: {
    case: string;
    resolution: string;
  };
}

export interface WorkflowActionRunGraphQueryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunGraphQuery;
  version: 1;
  params: {
    'graph-query-template-id': string;
    'query-inputs': ResolvedInputs;
    'generate-snapshot': boolean;
    async: boolean;
    timeout: number;
  };
}

export interface WorkflowActionRunReportAddressLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportAddressLookup;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportAdverseMediaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportAdverseMedia;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessLookup;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessNonprofitV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessNonprofit;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessOnlinePresenceV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessOnlinePresence;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessAdverseMediaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessAdverseMedia;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessAssociatedPersonsV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessAssociatedPersons;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessClassificationV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessClassification;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessIndustryClassificationV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessIndustryClassification;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessLiensLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessLiensLookup;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessRegistrationsLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessRegistrationsLookup;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessSocialMediaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessSocialMedia;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessWatchlistV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessWatchlist;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportBusinessWebsiteV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportBusinessWebsite;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportCryptoAddressWatchlistV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportCryptoAddressWatchlist;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportCustomListV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportCustomList;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportEmailAddressV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportEmailAddress;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportPhoneNumberV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportPhoneNumber;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportPoliticallyExposedPersonV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportPoliticallyExposedPerson;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportProfileV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportProfile;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportProfileNonAuthoritativeV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportProfileNonAuthoritative;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportScreeningV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportScreening;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportSocialMediaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportSocialMedia;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportSyntheticV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportSynthetic;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunReportWatchlistV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportWatchlist;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionRunVerificationDatabaseBusinessV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationDatabaseBusiness;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'complete-on-tentative': boolean;
  };
}

export interface WorkflowActionRunVerificationDatabaseBusinessFootprintV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionRunVerificationBusinessWebsiteFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionRunVerificationDatabasePhoneCarrierFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionRunVerificationDatabaseSerproFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams;
}

export interface WorkflowActionRunVerificationDatabaseTinFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'flow-state-attempt-count-token'?: string;
  };
}

export interface WorkflowActionRunVerificationDatabaseVatNumberFromInquiryV1
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry;
  version: 1;
  params: WorkflowCreateVerificationWithFieldsParams & {
    'flow-state-attempt-count-token'?: string;
  };
}

export interface WorkflowActionSubmitMiddeskBusinessLookupV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SubmitMiddeskBusinessLookup;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-template-type'?: ApiModel.ReportTemplateMiddeskBusinessLookup; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionTagObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.TagObject;
  version: 1;
  params: {
    object: string;
    ['tag-id']?: string | string[];
  };
}

export interface WorkflowActionRemoveTagV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RemoveTag;
  version: 1;
  params: {
    object: string;
    'tag-ids': string[];
  };
}

export interface WorkflowActionCopyTagsV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CopyTags;
  version: 1;
  params: {
    'from-object': string;
    'to-object': string;
    'exclude-tags'?: string[];
  };
}

export interface WorkflowActionTrmWalletScreeningV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.TrmWalletScreening;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    'report-template-type'?: ApiModel.ReportTemplateTrmWalletScreening; // Not used in backend. used for differentiate among generic report in FE.
  };
}

export interface WorkflowActionTrustDeviceFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.TrustDeviceFromInquiry;
  version: 1;
  params: {
    inquiry: string;
  };
}

export interface WorkflowActionUpdateAccountV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateAccount;
  version: 1;
  params: {
    inquiry: string;
    account: string;
    'input-fields': ResolvedInputs;
  };
}

export interface WorkflowActionUpdateAccountFieldsFromInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateAccountFieldsFromInquiry;
  version: 1;
  params: {
    inquiry: string;
    'account-to-inquiry-field-mapping': Record<string, string>;
  };
}

export interface WorkflowActionUpdateInquiryNextStepNameV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryNextStepName;
  version: 1;
  params: {
    inquiry: string;
    'next-step-name': string;
  };
}

export interface WorkflowActionUpdateInquiryFieldsFromVerificationDocument
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDocument;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
  };
}

export interface WorkflowActionUpdateInquiryFieldsFromVerificationDatabase
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
    // TODO: Copy from action config
    'inquiry-to-verification-field-mapping': Record<string, string>;
  };
}

export interface WorkflowActionUpdateInquiryFieldsFromVerificationDatabaseStandard
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabaseStandard;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
    // TODO: Copy from action config
    'inquiry-to-verification-field-mapping': Record<string, string>;
  };
}

export interface WorkflowActionUpdateInquiryFieldsFromVerificationGovernmentId
  extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
    // TODO: Copy from action config
    'inquiry-to-verification-field-mapping': Record<string, string>;
  };
}

export interface WorkflowActionUpdateInquiryFromPersonaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryFromPersona;
  version: 1;
  params: {
    inquiry: string;
    'fields-mapping': Record<string, string>;
  };
}

export interface WorkflowActionUpdateInquiryFieldsFromVerification extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.UpdateInquiryFieldsFromVerification;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
    'inquiry-to-verification-field-mapping': Record<string, string>;
  };
}

export interface WorkflowActionUpdateInquiryStateDatabaseError extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryStateDatabaseError;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
  };
}

export interface WorkflowActionUpdateInquiryStateDocumentError extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryStateDocumentError;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
  };
}

export interface WorkflowActionUpdateInquiryStateGovernmentIdError extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryStateGovernmentIdError;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
  };
}

export interface WorkflowActionUpdateInquiryStateSelfieError extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateInquiryStateSelfieError;
  version: 1;
  params: {
    inquiry: string;
    verification: string;
  };
}

export interface WorkflowActionSmsInquiryLinkV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SmsInquiryLink;
  version: 1;
  params: {
    inquiry: string;
    recipient?: string;
    message: string;
    'expires-in-minutes'?: number;
    'phone-number': string;
  };
}

export interface WorkflowActionRunReportGenericV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportGeneric;
  version: 1;
  params: {
    object: string;
    'report-template-token': string;
    'report-fields': ResolvedInputs;
    'report-step-action-key'?: WorkflowStepActionKey;
    // Not used in backend. used for differentiate among generic report in FE.
    'report-template-type'?:
      | ApiModel.ReportTemplateEquifaxOneview
      | ApiModel.ReportTemplateKyckrBusinessLookup
      | ApiModel.ReportTemplateMiddeskBusinessLookup
      | ApiModel.ReportTemplateSentilinkApplicationRisk
      | ApiModel.ReportTemplateSentilinkScores
      | ApiModel.ReportTemplateChainalysisAddressScreening
      | ApiModel.ReportTemplateMxAccount
      | ApiModel.ReportTemplateTrmWalletScreening;
  };
}

export interface WorkflowActionRunVerificationGenericV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunVerificationGeneric;
  version: 1;
  params: {
    // NOTE: We're moving away from `object` to `objects`
    // but `object` is kept around for compatibility
    objects?: string[];
    object?: string;
    'verification-template-version-id': string;
    'complete-on-tentative': boolean;
    'retry-window-seconds': number | null;
    // previously (before PLF-2061) RunVerification was centered around verification-template-version-id. moving forward
    // we are migrating this to verification-template-id.
    'verification-template-id': string;
    'verification-fields': Record<string, string>;
    'verification-type'?: Verification['type'];
    'verification-step-action-key'?: WorkflowStepActionKey;
    // Keep in sync with app/lib/workflow_action/run_verification_v1.rb
    'verification-meta'?: {
      document?: {
        'comparison-attributes': Record<string, string>;
      };
      'government-id'?: {
        'comparison-attributes': Record<string, string>;
      };
      database?: {
        'comparison-attributes': Record<string, string>;
      };
      selfie?: {
        'comparison-attributes': Record<string, string>;
      };
    };
    'debug-forced-status'?: string;
  };
}

export interface WorkflowActionSendConfirmationCodeV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SendConfirmationCode;
  version: 1;
  params: {
    verification: string;
  };
}

export interface WorkflowActionAttemptConfirmationCodeV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.AttemptConfirmationCode;
  version: 1;
  params: {
    verification: string;
    'confirmation-code': string;
  };
}

export enum WorkflowTimeUnit {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
}

export interface WorkflowActionScheduleWorkflowRunV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ScheduleWorkflowRun;
  version: 1;
  params: {
    'workflow-id': string;
    object: string;
    'input-fields': Record<string, string>;
    'time-offset': number;
    'time-unit': WorkflowTimeUnit;
  };
}

export interface WorkflowActionUpdateAccountFromUserInputV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateAccountFromUserInput;
  version: 1;
  params: {
    account: string;
    'account-fields': ResolvedInputs;
  };
}

export interface WorkflowActionHttpRequestV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.HttpRequest;
  version: 1;
  params: {
    url: string;
    method: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';
    body: string;
    'headers-v2'?: Record<string, string>[];
    'persona-secret'?: string;
    'return-schema': FieldSchemaJson | null;
    'retry-intervals': number[] | null; // TODO: (Kengo) promote to global config among action steps
    timeout: number | null;
  };
}

export interface WorkflowActionLabelObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.LabelObject;
  version: 1;
  params: {
    object: string;
  };
}

export interface WorkflowActionSendCaseMessageV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SendCaseMessage;
  version: 1;
  params: {
    'send-message-type': 'new' | 'reply';
    case?: string;
    'case-message'?: string;
    'case-message-template-token': string;
    'to-email-addresses': string[];
    'additional-cc-email-addresses': string[];
  };
}

export interface WorkflowActionSendEmailV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SendEmail;
  version: 1;
  params: {
    recipient: string;
    subject: string;
    'body-html': string;
    'body-text': string;
    'from-email-address': string;
    'smtp-address': string;
    'smtp-port': string;
    'smtp-tls': boolean;
    'smtp-user-name': string;
    'smtp-password': string;
    'smtp-domain': string;
  };
}

export interface WorkflowActionSendSmsV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SendSms;
  version: 1;
  params: {
    recipient: string;
    message: string;
  };
}

export interface WorkflowActionSendSlackMessageV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SendSlackMessage;
  version: 1;
  params: {
    url: string;
    message: string;
    override: string;
  };
}

export interface WorkflowActionSlackOAuthSendMessageV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SlackOAuthSendMessage;
  version: 1;
  params: {
    'external-integration-id': string;
    'recipient-type': string;
    'recipient-id': string;
    message: string;
  };
}

export enum ZendeskSupportedTicketStatuses {
  DEFAULT = 'default',
  NEW = 'new',
  OPEN = 'open',
  PENDING = 'pending',
  HOLD = 'hold',
  SOLVED = 'solved',
  CLOSED = 'closed',
}

export type StatusOptions =
  | ZendeskSupportedTicketStatuses.DEFAULT
  | ZendeskSupportedTicketStatuses.NEW
  | ZendeskSupportedTicketStatuses.OPEN
  | ZendeskSupportedTicketStatuses.PENDING
  | ZendeskSupportedTicketStatuses.HOLD
  | ZendeskSupportedTicketStatuses.SOLVED
  | ZendeskSupportedTicketStatuses.CLOSED;

export interface WorkflowActionZendeskAddTagsToTicketV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ZendeskAddTagsToTicket;
  version: 1;
  params: {
    'external-integration-id': string;
    'ticket-id': string;
    tags: string[];
  };
}

export interface WorkflowActionZendeskRemoveTagsFromTicketV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ZendeskRemoveTagsFromTicket;
  version: 1;
  params: {
    'external-integration-id': string;
    'ticket-id': string;
    tags: string[];
  };
}

export interface WorkflowActionZendeskTicketCreateV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ZendeskCreateTicket;
  version: 1;
  params: {
    'domain-url': string;
    'account-email': string;
    'api-token': string;
    subject?: string;
    body: string;
    public?: boolean;
    status?: 'default' | 'new' | 'open' | 'pending' | 'hold' | 'solved' | 'closed';
    'add-tags'?: string[];
  };
}

export interface WorkflowActionZendeskOAuthCreateTicketV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ZendeskOAuthCreateTicket;
  version: 1;
  params: {
    'external-integration-id': string;
    subject?: string;
    body: string;
    'is-public'?: boolean;
    status?: StatusOptions;
    tags?: string[];
    'requester-name'?: string;
    'requester-email'?: string;
  };
}

export interface WorkflowActionZendeskOAuthUpdateTicketV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ZendeskOAuthUpdateTicket;
  version: 1;
  params: {
    'external-integration-id': string;
    'ticket-id': string;
    body: string;
    'is-public'?: boolean;
    status?: StatusOptions;
    tags?: string[];
  };
}

export interface WorkflowActionZendeskTicketUpdateV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ZendeskUpdateTicket;
  version: 1;
  params: {
    'domain-url': string;
    'account-email': string;
    'api-token': string;
    'ticket-id': string;
    body: string;
    public?: boolean;
    status?: 'default' | 'new' | 'open' | 'pending' | 'hold' | 'solved' | 'closed';
    'add-tags'?: string[];
    'remove-tags'?: string[];
  };
}

export interface WorkflowActionHubspotOAuthCreateObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.HubspotOAuthCreateObject;
  version: 1;
  params: {
    'external-integration-id': string;
    'object-type': string;
    'object-properties': string;
    'associated-object-type'?: string;
    'associated-object-id'?: string;
  };
}

export interface WorkflowActionHubspotOAuthSearchObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.HubspotOAuthSearchObject;
  version: 1;
  params: {
    'external-integration-id': string;
    'object-type': string;
    'object-properties': string;
  };
}

export interface WorkflowActionHubspotOAuthUpdateObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.HubspotOAuthUpdateObject;
  version: 1;
  params: {
    'external-integration-id': string;
    'object-type': string;
    'object-id': string;
    'object-properties': string;
    'associated-object-type'?: string;
    'associated-object-id'?: string;
    'skip-on-blank-value': boolean;
  };
}

export interface WorkflowActionHubspotUpdateObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.HubspotUpdateObject;
  version: 1;
  params: {
    'access-token': string;
    'object-type': string;
    'object-id': string;
    'object-properties': string;
  };
}

export interface WorkflowActionSalesforceCreateObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SalesforceCreateObject;
  version: 1;
  params: {
    'domain-url': string;
    'client-id': string;
    'client-secret': string;
    username: string;
    password: string;
    'object-type': string;
    'object-properties': string;
  };
}

export interface WorkflowActionSalesforceOAuthCreateRecordV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SalesforceOAuthCreateRecord;
  version: 1;
  params: {
    'external-integration-id': string;
    'record-type': string;
    'record-properties': string;
  };
}

export interface WorkflowActionSalesforceOAuthSearchRecordV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SalesforceOAuthSearchRecord;
  version: 1;
  params: {
    'external-integration-id': string;
    'record-type': string;
    'record-properties': string;
  };
}

export interface WorkflowActionSalesforceOAuthUpdateRecordV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SalesforceOAuthUpdateRecord;
  version: 1;
  params: {
    'external-integration-id': string;
    'record-type': string;
    'record-id': string;
    'record-properties': string;
  };
}

export interface WorkflowActionSalesforceUpdateObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SalesforceUpdateObject;
  version: 1;
  params: {
    'domain-url': string;
    'client-id': string;
    'client-secret': string;
    username: string;
    password: string;
    'object-type': string;
    'object-id': string;
    'object-properties': string;
  };
}

export interface WorkflowActionCreateInquiryV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.CreateInquiry;
  version: 1;
  params: ExpirationIntervals & {
    'template-id': string;
    'theme-id': string;
    'theme-set-id'?: string;
    account: string;
    'redirect-uri': string;
    'inquiry-fields'?: ResolvedInputs;
    'initial-step-name'?: string;
  };
}

export interface WorkflowActionUpdateObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateObject;
  version: 1;
  params: {
    object: string;
    fields: StringKeyValue;
    values?: KeyValue;
    operators?: Record<string, string>;
    'skip-empty-fields'?: boolean;
  };
}

export interface WorkflowActionClearCaseSlaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.ClearCaseSla;
  version: 1;
  params: {
    case: string;
  };
}

export interface WorkflowActionSetCaseSlaV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SetCaseSla;
  version: 1;
  params: {
    case: string;
    'sla-seconds': number;
  };
}

export interface WorkflowActionSetRelationV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.SetRelation;
  version: 1;
  params: {
    object: string;
    'relation-schema-name': string;
    'assigned-object': string;
  };
}

export interface WorkflowActionUpdateCaseStatusV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UpdateCaseStatus;
  version: 1;
  params: {
    case: string;
    'state-ids': string[];
  };
}

export interface WorkflowActionRunReportWithParamsV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunReportWithParams;
  version: 1;
  params: {
    case: string;
    'account-id': string;
    'report-template-id': string;
    'report-fields': ResolvedInputs;
  };
}

export interface WorkflowActionFetchPersonaObjectV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.FetchPersonaObject;
  version: 1;
  params: {
    'object-type':
      | ApiModel.Account
      | ApiModel.Inquiry
      | ApiModel.Case
      | ApiModel.Report
      | ApiModel.Transaction
      | ApiModel.Verification
      | ApiModel.SAR;
    id: string;
    subtype?: WorkflowFieldObjectKey;
  };
}

export interface WorkflowActionGetAnalyticsMetricV1 extends WorkflowActionBase {
  key: DeprecatedWorkflowStepActionKey.GetAnalyticsMetric;
  version: 1;
  params: {
    metric: string;
    'inquiry-template-tokens'?: string[];
    'group-by'?: string;
  };
}

export interface WorkflowActionUnsetRelationV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.UnsetRelation;
  version: 1;
  params: {
    object: string;
    'relation-schema-name': string;
    'unset-object': string | null;
  };
}

export interface WorkflowActionFromConfig {
  key: GenericWorkflowAction['key'];
  version: number;
  params: Record<
    string,
    | string
    | string[]
    | null
    | boolean
    | number
    | Record<string, string>
    // (evan.qi) TODO remove the types below
    | JSONSchema7Object
    | RJSFSchema
    | UiSchema
  >;
}

export interface HttpPlatformIntegrationConfig extends GenericWorkflowAction {
  key: WorkflowActionRunHttpPlatformIntegrationV1['key'];
  integration: {
    vendor: string;
    operation_id: string;
  };
  'integration-configs': { label: string; value: string }[];
  request: {
    parametersSchema: RJSFSchema;
    parametersUiSchema: UiSchema;
    schema: RJSFSchema;
    uiSchema: UiSchema;
  };
  response: {
    schema: RJSFSchema;
    uiSchema: UiSchema;
  };
}

export interface WorkflowActionRunHttpPlatformIntegrationV1 extends WorkflowActionBase {
  key: WorkflowStepActionKey.RunHttpPlatformIntegration;
  params: {
    request_body: JSONSchema7Object | null;
    request_parameters: JSONSchema7Object;
    integration: HttpPlatformIntegrationConfig['integration'];
    integration_credential: string;
  };
}

export interface WorkflowActionRunHttpPlatformIntegrationV1Output {
  request: {
    path: string;
    body: JSONSchema7Object;
  };
  response: JSONSchema7Object;
  'response-status': {
    code: number;
    reason: string;
  };
}

export type DeprecatedWorkflowAction =
  | WorkflowActionCreateReportAdverseMediaV1
  | WorkflowActionCreateReportEmailAddressV1
  | WorkflowActionCreateReportPhoneNumberV1
  | WorkflowActionCreateReportProfileV1
  | WorkflowActionCreateReportSocialMediaV1
  | WorkflowActionCreateReportSyntheticV1
  | WorkflowActionCreateReportWatchlistV1
  | WorkflowActionResolveCaseV1
  | WorkflowActionUpdateInquiryFieldsFromVerification
  | WorkflowActionGetAnalyticsMetricV1;

export type WorkflowAction =
  | WorkflowActionAddCaseToCaseQueueV1
  | WorkflowActionAddCommentToCaseV1
  | WorkflowActionAddItemToListV1
  | WorkflowActionAddItemToListFaceV1
  | WorkflowActionApproveInquiryV1
  | WorkflowActionAssignCaseV1
  | WorkflowActionAttachObjectsV1
  | WorkflowActionCalculateV1
  | WorkflowActionChainalysisAddressScreeningV1
  | WorkflowActionClearbitBusinessLookupV1
  | WorkflowActionClearCaseSlaV1
  | WorkflowActionCoinbaseCheckCryptoRiskV1
  | WorkflowActionConfirmLatestVerificationEmailAddress
  | WorkflowActionConfirmLatestVerificationPhoneNumber
  | WorkflowActionCopyTagsV1
  | WorkflowActionCreateCaseV1
  | WorkflowActionCreateInquiryV1
  | WorkflowActionCreateSARV1
  | WorkflowActionCreateTransactionV1
  | WorkflowActionCreateDocumentGovernmentIdV1
  | WorkflowActionCreateVerificationAamvaFromInquiryV1
  | WorkflowActionCreateVerificationCertificateKoreaFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseAadhaarFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseStandardFromInquiryV1
  | WorkflowActionCreateVerificationDatabaseEcbsvFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdFinnishTrustNetworkFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdOneIdFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdSingpassFromInquiryV1
  | WorkflowActionCreateVerificationDigitalIdUkSharecodeFromInquiryV1
  | WorkflowActionCreateVerificationDocumentFromInquiryV1
  | WorkflowActionCreateVerificationEmailAddressFromInquiryV1
  | WorkflowActionCreateVerificationGovernmentIdFromInquiryV1
  | WorkflowActionCreateVerificationGovernmentIdNfcFromInquiryV1
  | WorkflowActionCreateVerificationPhoneNumberFromInquiryV1
  | WorkflowActionCreateVerificationSelfieFromInquiryV1
  | WorkflowActionEmailInquiryLinkV1
  | WorkflowActionEquifaxOneviewV1
  | WorkflowActionEvaluateCodeV1
  | WorkflowActionEvaluateCodeV2
  | WorkflowActionFetchPersonaObjectV1
  | WorkflowActionFindOrCreateAccountV1
  | WorkflowActionFindOrCreateTransactionV1
  | WorkflowActionSyncGraphV1
  | WorkflowActionHttpRequestV1
  | WorkflowActionHubspotOAuthCreateObjectV1
  | WorkflowActionHubspotOAuthSearchObjectV1
  | WorkflowActionHubspotOAuthUpdateObjectV1
  | WorkflowActionHubspotUpdateObjectV1
  | WorkflowActionGPTGenerateMessageV1
  | WorkflowActionListMatchV1
  | WorkflowActionLabelObjectV1
  | WorkflowActionMxAccountV1
  | WorkflowActionMxCreateUserV1
  | WorkflowActionMxGetMicrodepositStatusV1
  | WorkflowActionMxRequestConnectWidgetV1
  | WorkflowActionNovaCreditCashAtlasV1
  | WorkflowActionPauseReportsContinuousMonitoringV1
  | WorkflowActionProcessLatestVerificationCertificateKoreaV1
  | WorkflowActionProcessLatestVerificationDatabaseAadhaarV1
  | WorkflowActionProcessLatestVerificationDigitalIdFinnishTrustNetworkV1
  | WorkflowActionProcessLatestVerificationDigitalIdOneIdV1
  | WorkflowActionProcessLatestVerificationDigitalIdSingpassV1
  | WorkflowActionQueryV1
  | WorkflowActionRemoveItemFromListV1
  | WorkflowActionRedactObjectV1
  | WorkflowActionResendConfirmationCodeLatestVerificationEmailAddressV1
  | WorkflowActionRemoveTagV1
  | WorkflowActionResendConfirmationCodeLatestVerificationPhoneNumberV1
  | WorkflowActionRunGraphQueryV1
  | WorkflowActionRunKyckrBusinessLookupV1
  | WorkflowActionRunReportAddressLookupV1
  | WorkflowActionRunReportAdverseMediaV1
  | WorkflowActionRunReportBusinessAdverseMediaV1
  | WorkflowActionRunReportBusinessAssociatedPersonsV1
  | WorkflowActionRunReportBusinessClassificationV1
  | WorkflowActionRunReportBusinessIndustryClassificationV1
  | WorkflowActionRunReportBusinessLiensLookupV1
  | WorkflowActionRunReportBusinessLookupV1
  | WorkflowActionRunReportBusinessNonprofitV1
  | WorkflowActionRunReportBusinessOnlinePresenceV1
  | WorkflowActionRunReportBusinessRegistrationsLookupV1
  | WorkflowActionRunReportBusinessSocialMediaV1
  | WorkflowActionRunReportBusinessWatchlistV1
  | WorkflowActionRunReportBusinessWebsiteV1
  | WorkflowActionRunReportCryptoAddressWatchlistV1
  | WorkflowActionRunReportCustomListV1
  | WorkflowActionRunReportEmailAddressV1
  | WorkflowActionRunReportGenericV1
  | WorkflowActionRunReportPhoneNumberV1
  | WorkflowActionRunReportPoliticallyExposedPersonV1
  | WorkflowActionRunReportProfileV1
  | WorkflowActionRunReportProfileNonAuthoritativeV1
  | WorkflowActionRunReportSentilinkApplicationRiskV1
  | WorkflowActionRunReportSentilinkScoresV1
  | WorkflowActionRunReportScreeningV1
  | WorkflowActionRunReportSocialMediaV1
  | WorkflowActionRunReportSyntheticV1
  | WorkflowActionRunReportWatchlistV1
  | WorkflowActionRunReportWithParamsV1
  | WorkflowActionRunVerificationGenericV1
  | WorkflowActionScheduleWorkflowRunV1
  | WorkflowActionRunVerificationBusinessWebsiteFromInquiryV1
  | WorkflowActionRunVerificationDatabaseBusinessV1
  | WorkflowActionRunVerificationDatabaseBusinessFootprintV1
  | WorkflowActionRunVerificationDatabasePhoneCarrierFromInquiryV1
  | WorkflowActionRunVerificationDatabaseSerproFromInquiryV1
  | WorkflowActionRunVerificationDatabaseTinFromInquiryV1
  | WorkflowActionRunVerificationDatabaseVatNumberFromInquiryV1
  | WorkflowActionSalesforceCreateObjectV1
  | WorkflowActionSalesforceOAuthCreateRecordV1
  | WorkflowActionSalesforceOAuthSearchRecordV1
  | WorkflowActionSalesforceOAuthUpdateRecordV1
  | WorkflowActionSalesforceUpdateObjectV1
  | WorkflowActionSendCaseMessageV1
  | WorkflowActionSendConfirmationCodeV1
  | WorkflowActionSendEmailV1
  | WorkflowActionSendSlackMessageV1
  | WorkflowActionSlackOAuthSendMessageV1
  | WorkflowActionSendSmsV1
  | WorkflowActionNovaCreditCreditPassportV1
  | WorkflowActionSmsInquiryLinkV1
  | WorkflowActionSubmitMiddeskBusinessLookupV1
  | WorkflowActionTagObjectV1
  | WorkflowActionTrmWalletScreeningV1
  | WorkflowActionTrustDeviceFromInquiryV1
  | WorkflowActionUpdateAccountV1
  | WorkflowActionUpdateAccountFieldsFromInquiryV1
  | WorkflowActionUpdateAccountFromUserInputV1
  | WorkflowActionSetCaseSlaV1
  | WorkflowActionSetRelationV1
  | WorkflowActionStandardizeIdDetailsV1
  | WorkflowActionUnsetRelationV1
  | WorkflowActionUpdateCaseStatusV1
  | WorkflowActionUpdateInquiryFieldsFromVerificationDatabase
  | WorkflowActionUpdateInquiryFieldsFromVerificationDatabaseStandard
  | WorkflowActionUpdateInquiryFieldsFromVerificationDocument
  | WorkflowActionUpdateInquiryFieldsFromVerificationGovernmentId
  | WorkflowActionUpdateInquiryFromPersonaV1
  | WorkflowActionUpdateInquiryNextStepNameV1
  | WorkflowActionUpdateInquiryStateDatabaseError
  | WorkflowActionUpdateInquiryStateDocumentError
  | WorkflowActionUpdateInquiryStateGovernmentIdError
  | WorkflowActionUpdateInquiryStateSelfieError
  | WorkflowActionUpdateObjectV1
  | WorkflowActionAttemptConfirmationCodeV1
  | WorkflowActionZendeskOAuthCreateTicketV1
  | WorkflowActionZendeskOAuthUpdateTicketV1
  | WorkflowActionZendeskTicketCreateV1
  | WorkflowActionZendeskTicketUpdateV1
  | WorkflowActionZendeskAddTagsToTicketV1
  | WorkflowActionZendeskRemoveTagsFromTicketV1;

export type AllWorkflowAction =
  | WorkflowAction
  | NullWorkflowAction
  | DeprecatedWorkflowAction
  | WorkflowActionFromConfig
  | WorkflowActionRunHttpPlatformIntegrationV1;
export type StaticWorkflowAction = WorkflowAction | DeprecatedWorkflowAction;

export type ActionFor<
  ActionKey extends StaticWorkflowAction['key'],
  Union = StaticWorkflowAction,
> = Union extends {
  key: ActionKey;
}
  ? Union
  : never;

/*
 * JSON Logic compatible rules that have variable names compatible with
 * ObjectVariableResolver in the backend.
 */
export interface WorkflowConditional {
  token: string;
  name: string;
  rules: LogicBlockOrV2;
}

export interface WorkflowElse {
  token: string;
  name: 'Else';
  rules: true;
}

export enum WorkflowStepType {
  Action = 'workflow-step/action',
  Conditional = 'workflow-step/conditional',
  Input = 'workflow-step/input',
  Loop = 'workflow-step/loop',
  Module = 'workflow-step/module',
  Output = 'workflow-step/output',
  Parallel = 'workflow-step/parallel',
  Placeholder = 'workflow-step/placeholder',
  Wait = 'workflow-step/wait',
}

export enum FakeWorkflowStepType {
  MapOutput = 'fake-workflow-step/map-output',
}

export interface WorkflowWatchedEvent {
  'event-name'?: EventName;
  'event-object-step-token': string;
}

export enum WorkflowStepOnErrorAction {
  Continue = 'continue',
}

export interface WorkflowStepBase {
  token: string;
  type: WorkflowStepType | FakeWorkflowStepType;
  name: string;
  'on-error'?: WorkflowStepOnErrorAction;
}

export interface WorkflowStepAction<Action extends AllWorkflowAction = AllWorkflowAction>
  extends WorkflowStepBase {
  type: WorkflowStepType.Action;
  action: Action;
}

export interface WorkflowStepWait extends WorkflowStepBase {
  type: WorkflowStepType.Wait;
  'watched-events': WorkflowWatchedEvent[] | null;
  conditional?: WorkflowConditional;
  'wait-period-seconds'?: number;
  'error-on-expiration'?: boolean;
}

export interface WorkflowStepConditional extends WorkflowStepBase {
  type: WorkflowStepType.Conditional;
  branches: WorkflowStep[][];
  conditionals: (WorkflowConditional | WorkflowElse)[];
}

export enum WorkflowStepLoopType {
  ForEach = 'for_each',
  While = 'while',
  Map = 'map',
}

export enum WorkflowStepLoopModeType {
  SEQUENTIAL = 'sequential',
  PARALLEL = 'parallel',
}

export interface WorkflowStepLoopForEachSettings {
  type: WorkflowStepLoopType.ForEach;
  'array-path': string;
  'input-array-schema'?: JsonSchemaArray;
  mode: WorkflowStepLoopModeType;
}

export interface WorkflowStepLoopWhileSettings {
  type: WorkflowStepLoopType.While;
  conditional: WorkflowConditional;
  'max-iterations': number | null;
}

export interface WorkflowStepLoopMapSettings {
  type: WorkflowStepLoopType.Map;
  'array-path': string;
  'item-path': string;
  'input-array-schema'?: JsonSchemaArray;
  'output-step-token'?: string;
  'output-item-schema'?: JsonSchema;
  mode: WorkflowStepLoopModeType;
}

export interface WorkflowStepMapOutput extends WorkflowStepBase {
  type: FakeWorkflowStepType.MapOutput;
}

export interface WorkflowStepLoop extends WorkflowStepBase {
  type: WorkflowStepType.Loop;
  branch: WorkflowStep[];
  settings:
    | WorkflowStepLoopForEachSettings
    | WorkflowStepLoopWhileSettings
    | WorkflowStepLoopMapSettings;
}

export interface WorkflowStepModule extends WorkflowStepBase {
  type: WorkflowStepType.Module;
  'input-mappings': { 'input-token': string; 'method-chain': string | null; 'raw-value': any }[];
  'child-workflow-block-id': string;
}

export interface WorkflowStepParallel extends WorkflowStepBase {
  type: WorkflowStepType.Parallel;
  branches: WorkflowStep[][];
}

export const INPUT_STEP_TOKEN = 'input';
export interface WorkflowStepInput extends Omit<WorkflowStepBase, 'on-error'> {
  type: WorkflowStepType.Input;
  token: typeof INPUT_STEP_TOKEN;
  trigger?: Trigger;
  inputs: BlockInput[];
  filter?: TemplateFilter;
}

export interface WorkflowStepPlaceholder extends Omit<WorkflowStepBase, 'on-error'> {
  type: WorkflowStepType.Placeholder;
}

export interface WorkflowStepOutput extends Omit<WorkflowStepBase, 'on-error'> {
  type: WorkflowStepType.Output;
  token: `${string}${typeof OUTPUT_STEP_TOKEN}`;
}

export type ActualWorkflowStep =
  | WorkflowStepAction
  | WorkflowStepConditional
  | WorkflowStepLoop
  | WorkflowStepModule
  | WorkflowStepParallel
  | WorkflowStepWait;

// Steps that can actually be added by UI
export type WorkflowStep =
  | ActualWorkflowStep
  | WorkflowStepPlaceholder // CAUTION: This type doesn't exist on backend
  | WorkflowStepMapOutput; // CAUTION: This type doesn't exist on backend

// Steps that can add to the workflow context
export type ContextualWorkflowStep = WorkflowStepInput | WorkflowStep;

export type WorkflowStepAll =
  | WorkflowStep
  | WorkflowStepInput // CAUTION: This type doesn't exist on backend
  | WorkflowStepOutput; // CAUTION: This type doesn't exist on backend

export type ActualWorkflowStepType = Exclude<
  WorkflowStepType,
  WorkflowStepType.Placeholder | WorkflowStepType.Input | WorkflowStepType.Output
>;

export const WORKFLOW_STEP_NEW_ACTION_NAME: Record<ActualWorkflowStepType, string> = {
  [WorkflowStepType.Action]: 'Action Step',
  [WorkflowStepType.Conditional]: 'Conditional Step',
  [WorkflowStepType.Loop]: 'Loop Step',
  [WorkflowStepType.Module]: 'Module Step',
  [WorkflowStepType.Parallel]: 'Parallel Step',
  [WorkflowStepType.Wait]: 'Wait Step',
};

export const WORKFLOW_ACTION_TO_OBJECT: Partial<
  Record<StaticWorkflowStepActionKey, WorkflowFieldObjectKey>
> = {
  [WorkflowStepActionKey.ClearbitBusinessLookup]:
    WorkflowFieldObjectKey.ReportClearbitBusinessLookup,
  [WorkflowStepActionKey.CoinbaseCheckCryptoRisk]:
    WorkflowFieldObjectKey.ReportCoinbaseCheckCryptoRisk,
  [WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress]:
    WorkflowFieldObjectKey.VerificationEmailAddress,
  [WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber]:
    WorkflowFieldObjectKey.VerificationPhoneNumber,
  [WorkflowStepActionKey.CreateCase]: WorkflowFieldObjectKey.Case,
  [WorkflowStepActionKey.CreateDocumentGovernmentId]: WorkflowFieldObjectKey.DocumentGovernmentId,
  [WorkflowStepActionKey.CreateInquiry]: WorkflowFieldObjectKey.Inquiry,
  [WorkflowStepActionKey.CreateSAR]: WorkflowFieldObjectKey.SAR,
  [WorkflowStepActionKey.CreateTransaction]: WorkflowFieldObjectKey.Transaction,
  [WorkflowStepActionKey.CreateVerificationAamvaFromInquiry]:
    WorkflowFieldObjectKey.VerificationAamva,
  [WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry]:
    WorkflowFieldObjectKey.VerificationCertificateKorea,
  [WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabase,
  [WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseAadhaar,

  [WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseStandard,
  [WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseEcbsv,
  [WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry]:
    WorkflowFieldObjectKey.VerificationDigitalIdFinnishTrustNetwork,
  [WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry]:
    WorkflowFieldObjectKey.VerificationDigitalIdOneId,
  [WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry]:
    WorkflowFieldObjectKey.VerificationDigitalIdSingpass,
  [WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry]:
    WorkflowFieldObjectKey.VerificationDigitalIdUkSharecode,
  [WorkflowStepActionKey.CreateVerificationDocumentFromInquiry]:
    WorkflowFieldObjectKey.VerificationDocument,
  [WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry]:
    WorkflowFieldObjectKey.VerificationEmailAddress,
  [WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry]:
    WorkflowFieldObjectKey.VerificationGovernmentId,
  [WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry]:
    WorkflowFieldObjectKey.VerificationGovernmentIdNfc,
  [WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry]:
    WorkflowFieldObjectKey.VerificationPhoneNumber,
  [WorkflowStepActionKey.CreateVerificationSelfieFromInquiry]:
    WorkflowFieldObjectKey.VerificationSelfie,
  [WorkflowStepActionKey.FindOrCreateAccount]: WorkflowFieldObjectKey.Account,
  [WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea]:
    WorkflowFieldObjectKey.VerificationCertificateKorea,
  [WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar]:
    WorkflowFieldObjectKey.VerificationDatabaseAadhaar,
  [WorkflowStepActionKey.RunGraphQuery]: WorkflowFieldObjectKey.GraphQuery,
  [WorkflowStepActionKey.RunReportAddressLookup]: WorkflowFieldObjectKey.ReportAddressLookup,
  [WorkflowStepActionKey.RunReportAdverseMedia]: WorkflowFieldObjectKey.ReportAdverseMedia,
  [WorkflowStepActionKey.RunReportBusinessAdverseMedia]:
    WorkflowFieldObjectKey.ReportBusinessAdverseMedia,
  [WorkflowStepActionKey.RunReportBusinessAssociatedPersons]:
    WorkflowFieldObjectKey.ReportBusinessAssociatedPersons,
  [WorkflowStepActionKey.RunReportBusinessClassification]:
    WorkflowFieldObjectKey.ReportBusinessClassification,
  [WorkflowStepActionKey.RunReportBusinessIndustryClassification]:
    WorkflowFieldObjectKey.ReportBusinessIndustryClassification,
  [WorkflowStepActionKey.RunReportBusinessLiensLookup]:
    WorkflowFieldObjectKey.ReportBusinessLiensLookup,
  [WorkflowStepActionKey.RunReportBusinessLookup]: WorkflowFieldObjectKey.ReportBusinessLookup,
  [WorkflowStepActionKey.RunReportBusinessNonprofit]:
    WorkflowFieldObjectKey.ReportBusinessNonprofit,
  [WorkflowStepActionKey.RunReportBusinessOnlinePresence]:
    WorkflowFieldObjectKey.ReportBusinessOnlinePresence,
  [WorkflowStepActionKey.RunReportBusinessRegistrationsLookup]:
    WorkflowFieldObjectKey.ReportBusinessRegistrationsLookup,
  [WorkflowStepActionKey.RunReportBusinessSocialMedia]:
    WorkflowFieldObjectKey.ReportBusinessSocialMedia,
  [WorkflowStepActionKey.RunReportBusinessWatchlist]:
    WorkflowFieldObjectKey.ReportBusinessWatchlist,
  [WorkflowStepActionKey.RunReportBusinessWebsite]: WorkflowFieldObjectKey.ReportBusinessWebsite,
  [WorkflowStepActionKey.ChainalysisAddressScreening]:
    WorkflowFieldObjectKey.ReportChainalysisAddressScreening,
  [WorkflowStepActionKey.RunReportCryptoAddressWatchlist]:
    WorkflowFieldObjectKey.ReportCryptoAddressWatchlist,
  [WorkflowStepActionKey.RunReportCustomList]: WorkflowFieldObjectKey.ReportCustomList,
  [WorkflowStepActionKey.EquifaxOneview]: WorkflowFieldObjectKey.ReportEquifaxOneview,
  [WorkflowStepActionKey.RunReportEmailAddress]: WorkflowFieldObjectKey.ReportEmailAddress,
  [WorkflowStepActionKey.RunKyckrBusinessLookup]: WorkflowFieldObjectKey.ReportKyckrBusinessLookup,
  [WorkflowStepActionKey.MxAccount]: WorkflowFieldObjectKey.ReportMxAccount,
  [WorkflowStepActionKey.RunReportPhoneNumber]: WorkflowFieldObjectKey.ReportPhoneNumber,
  [WorkflowStepActionKey.RunReportPoliticallyExposedPerson]:
    WorkflowFieldObjectKey.ReportPoliticallyExposedPerson,
  [WorkflowStepActionKey.RunReportProfile]: WorkflowFieldObjectKey.ReportProfile,
  [WorkflowStepActionKey.RunReportProfileNonAuthoritative]:
    WorkflowFieldObjectKey.ReportProfileNonAuthoritative,
  [WorkflowStepActionKey.RunReportSocialMedia]: WorkflowFieldObjectKey.ReportSocialMedia,
  [WorkflowStepActionKey.RunReportScreening]: WorkflowFieldObjectKey.ReportScreening,
  [WorkflowStepActionKey.RunReportSentilinkApplicationRisk]:
    WorkflowFieldObjectKey.ReportSentilinkApplicationRisk,
  [WorkflowStepActionKey.RunReportSentilinkScores]: WorkflowFieldObjectKey.ReportSentilinkScores,
  [WorkflowStepActionKey.RunReportSynthetic]: WorkflowFieldObjectKey.ReportSynthetic,
  [WorkflowStepActionKey.TrmWalletScreening]: WorkflowFieldObjectKey.ReportTrmWalletScreening,
  [WorkflowStepActionKey.RunReportWatchlist]: WorkflowFieldObjectKey.ReportWatchlist,
  [WorkflowStepActionKey.RunVerificationDatabaseBusiness]:
    WorkflowFieldObjectKey.VerificationDatabaseBusiness,
  [WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseBusinessFootprint,
  [WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry]:
    WorkflowFieldObjectKey.VerificationBusinessWebsite,
  [WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier,
  [WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseSerpro,
  [WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseTin,
  [WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry]:
    WorkflowFieldObjectKey.VerificationDatabaseVatNumber,
  [WorkflowStepActionKey.SubmitMiddeskBusinessLookup]:
    WorkflowFieldObjectKey.ReportMiddeskBusinessLookup,

  // deprecated keys
  [DeprecatedWorkflowStepActionKey.CreateReportAdverseMedia]:
    WorkflowFieldObjectKey.ReportAdverseMedia,
  [DeprecatedWorkflowStepActionKey.CreateReportEmailAddress]:
    WorkflowFieldObjectKey.ReportEmailAddress,
  [DeprecatedWorkflowStepActionKey.CreateReportPhoneNumber]:
    WorkflowFieldObjectKey.ReportPhoneNumber,
  [DeprecatedWorkflowStepActionKey.CreateReportProfile]: WorkflowFieldObjectKey.ReportProfile,
  [DeprecatedWorkflowStepActionKey.CreateReportSocialMedia]:
    WorkflowFieldObjectKey.ReportSocialMedia,
  [DeprecatedWorkflowStepActionKey.CreateReportSynthetic]: WorkflowFieldObjectKey.ReportSynthetic,
  [DeprecatedWorkflowStepActionKey.CreateReportWatchlist]: WorkflowFieldObjectKey.ReportWatchlist,
};

export const WORKFLOW_ACTION_TO_VARIABLE: Partial<
  Record<
    StaticWorkflowStepActionKey | WorkflowActionRunHttpPlatformIntegrationV1['key'],
    WorkflowFieldVariableKey
  >
> = {
  [WorkflowStepActionKey.Calculate]: WorkflowFieldVariableKey.Calculate,
  [WorkflowStepActionKey.EvaluateCode]: WorkflowFieldVariableKey.Code,
  [WorkflowStepActionKey.ListMatch]: WorkflowFieldVariableKey.Matched,
  [WorkflowStepActionKey.HttpRequest]: WorkflowFieldVariableKey.HttpRequest,
  [WorkflowStepActionKey.MxCreateUser]: WorkflowFieldVariableKey.MxUser,
  [WorkflowStepActionKey.MxGetMicrodepositStatus]: WorkflowFieldVariableKey.MxMicrodepositStatus,
  [WorkflowStepActionKey.MxRequestConnectWidget]: WorkflowFieldVariableKey.MxWidgetUrl,
  [WorkflowStepActionKey.Query]: WorkflowFieldVariableKey.Code,
  [WorkflowStepActionKey.SalesforceOAuthCreateRecord]: WorkflowFieldVariableKey.SalesforceRecord,
  [WorkflowStepActionKey.SalesforceOAuthSearchRecord]: WorkflowFieldVariableKey.SalesforceRecord,
  [WorkflowStepActionKey.ZendeskOAuthCreateTicket]: WorkflowFieldVariableKey.ZendeskTicket,
  [WorkflowStepActionKey.GPTGenerateMessage]: WorkflowFieldVariableKey.GptGeneratedMessage,
  [WorkflowStepActionKey.SalesforceCreateObject]: WorkflowFieldVariableKey.IntegrationObjectId,
  [WorkflowStepActionKey.ZendeskCreateTicket]: WorkflowFieldVariableKey.IntegrationObjectId,
  [WorkflowStepActionKey.HubspotOAuthCreateObject]: WorkflowFieldVariableKey.HubspotObjectId,
  [WorkflowStepActionKey.HubspotOAuthSearchObject]: WorkflowFieldVariableKey.HubspotObjectId,
  [WorkflowStepActionKey.RunHttpPlatformIntegration]: WorkflowFieldVariableKey.PlatformIntegration,
  [WorkflowStepActionKey.StandardizeIdDetails]: WorkflowFieldVariableKey.StandardizeIdDetails,
};

export const VERIFICATION_MODEL_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY: Partial<
  Record<Verification['type'], WorkflowFieldObjectKey>
> = {
  [ApiModel.VerificationAamva]: WorkflowFieldObjectKey.VerificationAamva,
  [ApiModel.VerificationBusinessWebsite]: WorkflowFieldObjectKey.VerificationBusinessWebsite,
  [ApiModel.VerificationCertificateKorea]: WorkflowFieldObjectKey.VerificationCertificateKorea,
  [ApiModel.VerificationDatabase]: WorkflowFieldObjectKey.VerificationDatabase,
  [ApiModel.VerificationDatabaseBusiness]: WorkflowFieldObjectKey.VerificationDatabaseBusiness,
  [ApiModel.VerificationDatabaseEcbsv]: WorkflowFieldObjectKey.VerificationDatabaseEcbsv,
  [ApiModel.VerificationDatabasePhoneCarrier]:
    WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier,
  [ApiModel.VerificationDatabaseSerpro]: WorkflowFieldObjectKey.VerificationDatabaseSerpro,
  [ApiModel.VerificationDatabaseStandard]: WorkflowFieldObjectKey.VerificationDatabaseStandard,
  [ApiModel.VerificationDatabaseTin]: WorkflowFieldObjectKey.VerificationDatabaseTin,
  [ApiModel.VerificationDatabaseVatNumber]: WorkflowFieldObjectKey.VerificationDatabaseVatNumber,
  [ApiModel.VerificationDigitalIdUkSharecode]:
    WorkflowFieldObjectKey.VerificationDigitalIdUkSharecode,
  [ApiModel.VerificationDocument]: WorkflowFieldObjectKey.VerificationDocument,
  [ApiModel.VerificationEmailAddress]: WorkflowFieldObjectKey.VerificationEmailAddress,
  [ApiModel.VerificationGovernmentId]: WorkflowFieldObjectKey.VerificationGovernmentId,
  [ApiModel.VerificationGovernmentIdNfc]: WorkflowFieldObjectKey.VerificationGovernmentIdNfc,
  [ApiModel.VerificationMobileDriversLicense]:
    WorkflowFieldObjectKey.VerificationMobileDriversLicense,
  [ApiModel.VerificationPhoneNumber]: WorkflowFieldObjectKey.VerificationPhoneNumber,
  [ApiModel.VerificationSelfie]: WorkflowFieldObjectKey.VerificationSelfie,
};

export type WorkflowStepWaitEvent =
  | EventName.CaseAssigned
  | EventName.CaseResolved
  | EventName.CaseStatusUpdated
  | EventName.GraphQuerySubmitted
  | EventName.GraphQueryErrored
  | EventName.GraphQueryCompleted
  | EventName.GraphQueryTimedOut
  | EventName.InquiryApproved
  | EventName.InquiryCompleted
  | EventName.InquiryDeclined
  | EventName.InquiryExpired
  | EventName.InquiryFailed
  | EventName.InquiryMarkedForReview
  | EventName.InquiryStarted
  | EventName.ReportAdverseMediaErrored
  | EventName.ReportAdverseMediaReady
  | EventName.ReportBusinessIndustryClassificationErrored
  | EventName.ReportBusinessIndustryClassificationReady
  | EventName.ReportBusinessIndustryClassificationSubmitted
  | EventName.ReportEmailAddressErrored
  | EventName.ReportEmailAddressReady
  | EventName.ReportMiddeskReady
  | EventName.ReportMiddeskErrored
  | EventName.ReportPhoneNumberErrored
  | EventName.ReportPhoneNumberReady
  | EventName.ReportPoliticallyExposedPersonErrored
  | EventName.ReportPoliticallyExposedPersonReady
  | EventName.ReportProfileErrored
  | EventName.ReportProfileReady
  | EventName.ReportProfileNonAuthoritativeReady
  | EventName.ReportProfileNonAuthoritativeErrored
  | EventName.ReportSocialMediaErrored
  | EventName.ReportSocialMediaReady
  | EventName.ReportSyntheticErrored
  | EventName.ReportSyntheticReady
  | EventName.ReportWatchlistErrored
  | EventName.ReportWatchlistReady
  | EventName.SARFiled
  | EventName.TransactionLabeled
  | EventName.TransactionRedacted
  | EventName.TransactionStatusUpdated
  | EventName.VerificationCanceled
  | EventName.VerificationCreated
  | EventName.VerificationFailed
  | EventName.VerificationPassed
  | EventName.VerificationRequiresRetry
  | EventName.VerificationSubmitted
  | EventName.VerificationTentativelyPassed
  | EventName.VerificationTentativelyFailed;

// A separate const exists for fetch action instead of using WORKFLOW_WAITABLE_EVENT_FOR_ACTION
// since available wait events are contingent on fetch step's configuration (i.e. param['object-type'])
export const WORKFLOW_WAITABLE_EVENT_FOR_FETCH_ACTION: Partial<
  Record<ApiModel, WorkflowStepWaitEvent[]>
> = {
  [ApiModel.Account]: [],
  [ApiModel.Case]: [EventName.CaseAssigned, EventName.CaseResolved, EventName.CaseStatusUpdated],
  [ApiModel.Inquiry]: [
    EventName.InquiryApproved,
    EventName.InquiryCompleted,
    EventName.InquiryDeclined,
    EventName.InquiryExpired,
    EventName.InquiryFailed,
    EventName.InquiryMarkedForReview,
  ],
  [ApiModel.Report]: [
    EventName.ReportBusinessIndustryClassificationErrored,
    EventName.ReportBusinessIndustryClassificationReady,
    EventName.ReportBusinessIndustryClassificationSubmitted,
  ],
  [ApiModel.Transaction]: [EventName.TransactionRedacted, EventName.TransactionStatusUpdated],
  [ApiModel.SAR]: [EventName.SARFiled],
  [ApiModel.Verification]: [
    EventName.VerificationFailed,
    EventName.VerificationCreated,
    EventName.VerificationSubmitted,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationFailed,
    EventName.VerificationCanceled,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
};

export const WORKFLOW_WAITABLE_EVENT_FOR_OBJECT: Partial<
  Record<WorkflowFieldObjectKey, WorkflowStepWaitEvent[]>
> = {
  [WorkflowFieldObjectKey.Case]: [
    EventName.CaseAssigned,
    EventName.CaseResolved,
    EventName.CaseStatusUpdated,
  ],
  [WorkflowFieldObjectKey.Inquiry]: [
    EventName.InquiryApproved,
    EventName.InquiryCompleted,
    EventName.InquiryDeclined,
    EventName.InquiryExpired,
    EventName.InquiryFailed,
    EventName.InquiryMarkedForReview,
  ],
  [WorkflowFieldObjectKey.SAR]: [EventName.SARFiled],
  [WorkflowFieldObjectKey.Transaction]: [EventName.TransactionStatusUpdated],
  [WorkflowFieldObjectKey.VerificationAamva]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationCertificateKorea]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationDatabase]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationDatabaseStandard]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationDatabaseEcbsv]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationDocument]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationEmailAddress]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationGovernmentId]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationGovernmentIdNfc]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationPhoneNumber]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationSelfie]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.GraphQuery]: [
    EventName.GraphQuerySubmitted,
    EventName.GraphQueryCompleted,
    EventName.GraphQueryErrored,
    EventName.GraphQueryTimedOut,
  ],
  [WorkflowFieldObjectKey.VerificationDatabasePhoneCarrier]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationDatabaseSerpro]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.VerificationDatabaseTin]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
  [WorkflowFieldObjectKey.ReportMiddeskBusinessLookup]: [
    EventName.ReportMiddeskReady,
    EventName.ReportMiddeskErrored,
  ],
  [WorkflowFieldObjectKey.ReportAdverseMedia]: [EventName.ReportAdverseMediaReady],
  [WorkflowFieldObjectKey.ReportEmailAddress]: [EventName.ReportEmailAddressReady],
  [WorkflowFieldObjectKey.ReportPhoneNumber]: [EventName.ReportPhoneNumberReady],
  [WorkflowFieldObjectKey.ReportProfile]: [EventName.ReportProfileReady],
  [WorkflowFieldObjectKey.ReportProfileNonAuthoritative]: [
    EventName.ReportProfileNonAuthoritativeReady,
  ],
  [WorkflowFieldObjectKey.ReportSocialMedia]: [EventName.ReportSocialMediaReady],
  [WorkflowFieldObjectKey.ReportSynthetic]: [EventName.ReportSyntheticReady],
  [WorkflowFieldObjectKey.ReportWatchlist]: [EventName.ReportWatchlistReady],
};

export const WORKFLOW_WAITABLE_EVENT_FOR_ACTION: Partial<
  Record<WorkflowStepActionKey, WorkflowStepWaitEvent[]>
> = {
  [WorkflowStepActionKey.RunReportGeneric]: [
    // NB: Business Industry Classification Reports are only run from the Generic Run Report step.
    EventName.ReportBusinessIndustryClassificationErrored,
    EventName.ReportBusinessIndustryClassificationReady,
  ],
  [WorkflowStepActionKey.RunVerificationGeneric]: [
    EventName.VerificationCanceled,
    EventName.VerificationCreated,
    EventName.VerificationFailed,
    EventName.VerificationPassed,
    EventName.VerificationRequiresRetry,
    EventName.VerificationSubmitted,
    EventName.VerificationTentativelyPassed,
    EventName.VerificationTentativelyFailed,
  ],
};

export const WORKFLOW_WAITABLE_EVENT_FOR_TRIGGER: Partial<
  Record<EventName, WorkflowStepWaitEvent[]>
> = {
  [EventName.InquiryCreated]: [
    EventName.InquiryStarted,
    EventName.InquiryCompleted,
    EventName.InquiryExpired,
    EventName.InquiryFailed,
    EventName.InquiryApproved,
    EventName.InquiryDeclined,
    EventName.InquiryMarkedForReview,
  ],
  [EventName.InquiryCompleted]: [
    EventName.InquiryApproved,
    EventName.InquiryDeclined,
    EventName.InquiryMarkedForReview,
  ],
  [EventName.CaseCreated]: [
    EventName.CaseAssigned,
    EventName.CaseResolved,
    EventName.CaseStatusUpdated,
  ],
  [EventName.CaseStatusUpdated]: [EventName.CaseAssigned, EventName.CaseResolved],
  [EventName.SARCreated]: [EventName.SARFiled],
  [EventName.TransactionCreated]: [
    EventName.TransactionLabeled,
    EventName.TransactionStatusUpdated,
  ],
};

// This list is the intersection between WorkflowStepWaitEvent and Event::REPEATABLES
export const WORKFLOW_WAITABLE_REPEATABLE_EVENT: EventName[] = [
  EventName.CaseAssigned,
  EventName.CaseResolved,
  EventName.CaseStatusUpdated,
  EventName.TransactionStatusUpdated,
];

export const newPlaceholderStep = (): WorkflowStepPlaceholder => {
  return {
    type: WorkflowStepType.Placeholder,
    token: generateWorkflowToken(),
    name: 'Placeholder',
  };
};

export const newWorkflowConditional = (name: string): WorkflowConditional => {
  return {
    // NOTE: What should this actually be?
    token: `wfc-${generateWorkflowToken()}`,
    name: name,
    rules: {
      or: [
        {
          and: [{}],
        },
      ],
    },
  };
};

export const OUTPUT_STEP_TOKEN = 'output';
export const outputStep: WorkflowStepOutput = {
  token: OUTPUT_STEP_TOKEN,
  type: WorkflowStepType.Output,
  name: 'Output Data',
};

export enum WorkflowRunStepErrorType {
  ConfigurationError = 'configuration-error',
  EvaluationError = 'evaluation-error',
  ResourceError = 'resource-error',
  InvalidSchemaError = 'invalid-schema-error',
  InvalidStateTransitionError = 'invalid-state-transition-error',
  InvalidInputError = 'invalid-input-error',
  InvalidObjectStateError = 'invalid-object-state-error',
  MissingInputError = 'missing-input-error',
  UnresolvedPathError = 'unresolved-path-error',
  UnknownError = 'unknown-error',
  RetryableResourceError = 'retryable-resource-error',
  ResourceExhaustedError = 'resource-exhausted-error',
  ResourceUnavailableError = 'resource-unavailable-error',
  // Note: below to deprecate
  InvalidStepConfigError = 'invalid-step-config-error',
  UnexpectedParameterError = 'unexpected-parameter-error',
}

interface WorkflowRunStepErrorBase {
  message: string;
  'continued-after-error'?: boolean;
}

export interface WorkflowRunStepInvalidStepConfigError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.InvalidStepConfigError;
  attributes: {
    'errored-field'?: string;
    'errored-fields'?: string[];
  };
}

export interface WorkflowRunStepEvaluationError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.EvaluationError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepResourceError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.ResourceError;
  attributes: Record<string, never>;
}

export interface WorkflowRunInvalidSchemaError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.InvalidSchemaError;
  attributes: Record<string, never>;
}

export interface WorkflowInvalidStateTransitionError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.InvalidStateTransitionError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepInvalidInputError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.InvalidInputError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepInvalidObjectStateError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.InvalidObjectStateError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepMissingInputError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.MissingInputError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepRetryableResourceError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.RetryableResourceError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepResourceExhaustedError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.ResourceExhaustedError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepResourceUnavailableError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.ResourceUnavailableError;
  attributes: Record<string, never>;
}

export enum UnexpectedParameterErrorFieldReason {
  Invalid = 'invalid',
  Blank = 'blank',
  Unnecessary = 'unnecessary',
}

export interface WorkflowRunStepUnexpectedParameterError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.UnexpectedParameterError;
  attributes: {
    'field-errors'?: {
      name: string;
      reason: UnexpectedParameterErrorFieldReason;
    }[];
  };
}

export interface WorkflowRunStepUnresolvedPathError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.UnresolvedPathError;
  attributes: {
    path: string;
  };
}

export interface WorkflowRunStepConfigurationError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.ConfigurationError;
  attributes: Record<string, never>;
}

export interface WorkflowRunStepUnknownError extends WorkflowRunStepErrorBase {
  type: WorkflowRunStepErrorType.UnknownError;
  attributes: Record<string, never>;
}

export type WorkflowRunStepError =
  | WorkflowRunStepInvalidStepConfigError
  | WorkflowRunStepUnexpectedParameterError
  | WorkflowRunStepUnresolvedPathError
  | WorkflowRunStepConfigurationError
  | WorkflowRunStepUnknownError
  | WorkflowRunStepEvaluationError
  | WorkflowRunStepResourceError
  | WorkflowRunInvalidSchemaError
  | WorkflowInvalidStateTransitionError
  | WorkflowRunStepInvalidInputError
  | WorkflowRunStepInvalidObjectStateError
  | WorkflowRunStepMissingInputError
  | WorkflowRunStepRetryableResourceError
  | WorkflowRunStepResourceExhaustedError
  | WorkflowRunStepResourceUnavailableError;
